/**
 * Generic CRUD list composable untuk halaman Settings & sejenisnya.
 *
 * Reduce boilerplate: setiap halaman Settings/* punya pola identik:
 *   - data() page, limit, search, data, loading
 *   - created() this.getListData(page, limit)
 *   - methods: getListData, pageNavHandler, tableInputSearchHandler
 *
 * Pemakaian:
 *   setup() {
 *     const crud = useCrudList(useBranchAdminList, fetchBranchById);
 *     return crud;
 *   }
 *
 * Return:
 *   listParams (reactive: page, limit, search)
 *   data, loading, refetch
 *   pageNav(type), searchHandler(event), fetchById(id)
 */
import { reactive, computed } from "@vue/composition-api";
import { useQueryClient } from "@tanstack/vue-query";

export function useCrudList(useListComposable, fetchByIdFn, options = {}) {
  const listParams = reactive({
    page: 1,
    limit: 10,
    pagination: true,
    search: "",
    ...(options.initialParams || {}),
  });

  const query = useListComposable(computed(() => ({ ...listParams })));
  const qc = useQueryClient();

  /* Detail by ID — cached 5 menit */
  async function fetchById(id, queryKeyExtra) {
    if (!fetchByIdFn) return null;
    const key = queryKeyExtra || ["crud-detail", id];
    return qc.fetchQuery({
      queryKey: key,
      queryFn: () => fetchByIdFn(id),
      staleTime: 5 * 60 * 1000,
    });
  }

  /* Pagination & search handlers */
  function pageNav(type) {
    if (type === "previous" && listParams.page > 1) listParams.page -= 1;
    else if (type === "next") listParams.page += 1;
  }

  /* Debounce search: tunggu 400ms setelah berhenti mengetik baru update
     listParams (yang memicu refetch via queryKey), supaya tidak request
     tiap keystroke. Delay bisa di-override via options.searchDebounce. */
  const searchDelay =
    typeof options.searchDebounce === "number" ? options.searchDebounce : 400;
  let searchTimer = null;

  function searchHandler(event) {
    const value = event && event.target ? event.target.value : event;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      if (!value) {
        listParams.search = "";
      } else {
        listParams.search = value;
        listParams.page = 1;
      }
    }, searchDelay);
  }

  /* Alias `crudData` agar tidak bentrok dengan Vue Options data() */
  return {
    listParams,
    crudData: query.data,
    crudIsLoading: query.isLoading,
    crudIsFetching: query.isFetching,
    crudError: query.error,
    crudRefetch: query.refetch,
    pageNav,
    searchHandler,
    fetchById,
    qc,
  };
}

export default useCrudList;
